import { PurePtr } from '@pure-ptr/core';
import React, { useState } from 'react';
import { DataTableHeader } from '../dataTableHeader';
import { Field } from '../dataTableHeader/DataTableHeader';
import { TableFilterState } from '../XDataGrid';

interface AdvancedTableFiltersProps {
    fields: Field[];
    pageName: string;
    pending: boolean;
    filtersPtr?: PurePtr<TableFilterState['filters']>;
    refreshCriteriaList?: ( setDefault : boolean ) => void;
    intl: any;
    criteria: any;
    selectedCriteria: any;
    criteriasSaveEnabled: boolean;
    userSessionId?:any;
    actions: {
        saveCriteriaFilter: (criteria: any, callback: () => void) => void;
        updateCriteriaFilter: (criteria: any, callback: () => void) => void;
        searchGetByName: (name: { name: string },callback?: () => void) => void;
    };
}

export default function AdvancedTableFilters({
    fields, pageName, 
    filtersPtr,
    pending, intl,
    selectedCriteria, 
    refreshCriteriaList,
    criteriasSaveEnabled, userSessionId,
    actions: { saveCriteriaFilter, updateCriteriaFilter }
}: AdvancedTableFiltersProps) {
    const filterPtr = filtersPtr.at('filter'),
        { filter, customFilter } = filtersPtr.value;

    /**
     * Staged filters are those shown in the UI panel.
     * They include both committed filters (from filterPtr) and pending changes
     * (additions/updates via the + button).
     * The API only fires when filterPtr.set() is called.
     */
    const [stagedFilters, setStagedFilters] = React.useState<any[]>(filter);

    // Sync staged filters with committed filters when they change externally (e.g. AI mode)
    React.useEffect(() => {
        setStagedFilters(filter);
    }, [filter]);

    function handleFilter(newFilter, isAddFilter) {
        if (pending) return;

        const hasNewFilter = newFilter?.equator && newFilter?.field && newFilter?.value !== undefined;
        let nextFilters = stagedFilters;

        if (hasNewFilter) {
            nextFilters = [newFilter, ...stagedFilters];
        }

        if (isAddFilter) {
            // Stage only (no API call)
            setStagedFilters(nextFilters);
        } else {
            // Commit all changes (triggers API)
            filterPtr.set(nextFilters);
        }
    }

    function handleDeleteFilter(deletedFilter, index) {
        const nextFilters = stagedFilters.filter((_, i) => i !== index);
        setStagedFilters(nextFilters);
        filterPtr.set(nextFilters);
    }

    function handleUpdateFilter(newFilterObj, index, isAddFilter) {
        if (index === null) return;

        const nextFilters = stagedFilters.map((f, i) => (i === index ? newFilterObj : f));
        
        if (isAddFilter) {
            // + button clicked: update the UI only
            setStagedFilters(nextFilters);
        } else {
            // Update button clicked: commit everything
            filterPtr.set(nextFilters);
        }
    }

    function handleFilterClear() {
        setStagedFilters([]);
        filtersPtr.update( prev => ({
            ...prev,
            selectedFilterId: null,
            filter: [],
        }));
    }

    function SaveFilterCallback(handleClose, setDefault) {
        handleClose();
        refreshCriteriaList && refreshCriteriaList( setDefault );    
    }

    function handleFilterSave(filterCriteria, handleClose, existingCriteria) {
        let searchCriteria = {
            ...filterCriteria,
            name: pageName,
        }

        if (existingCriteria && existingCriteria.id) {
            updateCriteriaFilter(searchCriteria, () => {
                SaveFilterCallback(handleClose, filterCriteria.isDefault )
            });
        } 
        else {
            saveCriteriaFilter(searchCriteria, () => {
                SaveFilterCallback(handleClose, filterCriteria.isDefault)
            });
        }
    }

    return (
        <DataTableHeader
            fields={fields}
            intl={intl}
            filter={stagedFilters}
            significance={filtersPtr.value?.significance}
            filtersPtr={filtersPtr}
            customFilter={customFilter}
            onFilterChange={handleFilter}
            onFilterDelete={handleDeleteFilter}
            onFilterClear={handleFilterClear}
            onFilterSave={handleFilterSave}
            onFilterUpdate={handleUpdateFilter}
            criteria={selectedCriteria}
            saveEnabled={criteriasSaveEnabled}
            pending={pending}
            pageName={pageName}
            userSessionId={userSessionId}
        />
    );
};                   